/**
 * Step5_GeoFeatures.js — ترجیحات محیطی
 *
 * Flow:
 *  1. Shows each geo-feature (for_matching=true) as a friendly Persian question
 *     with radio-style answer cards. Answers map to numeric values via
 *     geoFeatureMapping.js.
 *  2. Every question the user answers (non-null choice) becomes a "selected"
 *     item that appears in a draggable ranker below, so they can set priority
 *     order.
 *  3. On Next → PUT /requests/{id}/geo_features/ with ranked items.
 */

import { useState, useEffect } from 'react';
import { api, apiRoutes } from '../../../api';
import { upsertGeoFeatures } from '../services/propertyRequestService';
import HardAmenityInput, { allRangeKeys } from '../components/HardAmenityInput';

export default function Step5_GeoFeatures({ requestId, onNext, onBack, initialData = {} }) {
    const [allFeatures, setAllFeatures] = useState([]);
    // { [featureId]: value }  — value is array of bucket keys ([] = مهم نیست) or null
    const [geoValues, setGeoValues]       = useState(() => {
        const init = {};
        (initialData.geo_features || []).forEach(item => { init[item.geo_feature] = item.value; });
        return init;
    });
    const [geoRequired, setGeoRequired]   = useState(() => {
        const init = {};
        (initialData.geo_features || []).forEach(item => { init[item.geo_feature] = item.required; });
        return init;
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        api.get(apiRoutes.geo.features, { params: { for_matching: true } })
            .then(r => { setAllFeatures(r.data?.results ?? r.data); })
            .catch(() => setError('خطا در بارگذاری ویژگی‌های محیطی.'))
            .finally(() => setLoading(false));
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleNext = async () => {
        setError('');
        setSaving(true);
        try {
            // Build items for all features; skip features with null value
            const items = allFeatures
                .filter(f => geoValues[f.id] !== undefined)
                .map((f, idx) => ({
                    geo_feature: f.id,
                    rank:        idx + 1,
                    required:    geoRequired[f.id] ?? false,
                    value:       geoValues[f.id],
                }));
            if (items.length) await upsertGeoFeatures(requestId, items);
            onNext({ geo_features: items });
        } catch {
            setError('خطا در ذخیره ترجیحات. لطفاً دوباره تلاش کنید.');
        } finally {
            setSaving(false);
        }
    };

    // Only show features that have hcm_range_definition or are boolean
    const displayFeatures = allFeatures.filter(f =>
        f.hcm_mapping_type === 'boolean' ||
        (f.hcm_range_definition && Object.keys(f.hcm_range_definition).length > 0)
    );

    return (
        <div>
            <h2 className="prw-step-title">ترجیحات محیطی</h2>
            <p className="prw-step-desc">
                برای هر ویژگی گزینه‌های مورد نظر را انتخاب کنید. «مهم نیست» یعنی فیلتر اعمال نمی‌شود.
            </p>

            {loading ? (
                <p className="prw-step-desc">در حال بارگذاری...</p>
            ) : displayFeatures.length === 0 ? (
                <p className="prw-step-desc" style={{ color: '#90a4ae' }}>
                    در حال حاضر ویژگی محیطی تعریف‌شده‌ای برای پیکربندی وجود ندارد.
                </p>
            ) : (
                <div className="s5g-feature-list">
                    {displayFeatures.map(feature => {
                        const allKeys = allRangeKeys(feature.hcm_range_definition);
                        const currentVal = geoValues[feature.id] ?? allKeys;
                        const isRequired = geoRequired[feature.id] ?? false;
                        return (
                            <div key={feature.id} className="s5g-feature-item">
                                <div className="s5g-feature-header">
                                    <span className="s5g-feature-name">
                                        {feature.hcm_property_request_ask || feature.description || feature.name_fa || feature.name}
                                    </span>
                                    <button
                                        type="button"
                                        className={`ar-req-btn ${isRequired ? 'ar-req-on' : ''}`}
                                        onClick={() => setGeoRequired(prev => ({
                                            ...prev, [feature.id]: !prev[feature.id]
                                        }))}
                                    >
                                        {isRequired ? 'الزامی ✓' : 'اختیاری'}
                                    </button>
                                </div>
                                <HardAmenityInput
                                    amenity={{
                                        hcm_mapping_type:     feature.hcm_mapping_type,
                                        hcm_range_definition: feature.hcm_range_definition,
                                        categorical_options:  {},
                                        value_type:           feature.feature_type,
                                    }}
                                    value={currentVal}
                                    onChange={(val, req) => {
                                        setGeoValues(prev => ({ ...prev, [feature.id]: val }));
                                        setGeoRequired(prev => ({ ...prev, [feature.id]: req }));
                                    }}
                                />
                            </div>
                        );
                    })}
                </div>
            )}

            {error && <p className="prw-error">{error}</p>}

            <div className="prw-nav">
                <button className="prw-btn prw-btn-secondary" onClick={onBack}>→ مرحله قبل</button>
                <button
                    className="prw-btn prw-btn-primary"
                    onClick={handleNext}
                    disabled={saving || loading}
                >
                    {saving ? 'در حال ذخیره...' : 'مرحله بعد ←'}
                </button>
            </div>
        </div>
    );
}
